// O ACEITE E O PORTÃO DE DATA DO CADASTRO — `T8.14`, e dois dos cinco itens do achado.
//
// Medido em produção em 25/09/2026, deslogado, em `/cadastro`:
//
//   · ZERO caixas de seleção na página. Não há aceite dos Termos nem da Política, e a própria
//     `/lgpd` diz que isso é exigido. Os três links legais existem — mas só no rodapé, onde
//     ninguém os lê ao criar conta.
//   · o campo de data tem `max` igual a HOJE, `min` igual a `1900-01-01`. Nas duas pontas o
//     calendário OFERECE datas que o formulário depois recusa: `motivoDeRecusa` exige 6 anos e
//     no máximo 120.
//
// ── A DIFERENÇA ENTRE "ACEITA" E "DEIXA ESCOLHER", QUE O RELATÓRIO NÃO PÔDE MEDIR ────────────
//
// O relatório diz que *"o campo de data aceita a data de hoje"* e emenda: *"não consegui confirmar
// sem criar uma conta"*. Ele não confirmou, e fez bem em dizer. Medido no código, a recusa
// EXISTE — `motivoDeRecusa` barra no envio. O defeito é menor e é outro: **o calendário promete o
// que o formulário vai negar**, e a pessoa só descobre depois de preencher tudo.
//
// Então o conserto não é criar a regra; é fazer o campo dizer a mesma coisa que a regra. Um portão
// que só fala no fim é um portão que ensina a errar.
//
// ── POR QUE O ACEITE É UM MÓDULO, E NÃO UM `<input type="checkbox" required>` ─────────────────
//
// Porque consentimento tem regras que um `required` não carrega, e três delas são fáceis de violar
// sem perceber. Estão nos testes ao lado, e a primeira é a que importa mais: **a caixa nunca pode
// vir marcada.** Consentimento pré-marcado não é consentimento — é o mesmo defeito do "Deixar
// público" que vinha marcado numa plataforma de criança (T8.13), do outro lado do produto.

import dayjs from "dayjs";
import { idadeEmAnos, IDADE_MAXIMA, IDADE_MINIMA } from "../../api/idade";

/** O que o formulário precisa saber para deixar criar conta. */
export interface EstadoDoAceite {
  /** A pessoa marcou a caixa dos Termos e da Política. */
  aceitou: boolean;
  /** `true` na edição de dados de quem já tem conta — ali o aceite já aconteceu. */
  editando: boolean;
}

/**
 * Por que o cadastro não pode seguir, ou `null` quando pode.
 *
 * A mensagem diz o que fazer, não só o que está errado — a mesma régua do `motivoDeRecusa`.
 */
export function motivoDeRecusaDoAceite(e: EstadoDoAceite): string | null {
  // Quem edita os próprios dados já aceitou quando criou a conta. Pedir de novo transformaria uma
  // troca de cidade num novo consentimento, e ainda deixaria a pessoa presa se ela não marcasse.
  if (e.editando) return null;
  if (!e.aceitou) {
    return "Para criar a conta é preciso aceitar os Termos de uso e a Política de privacidade.";
  }
  return null;
}

/**
 * As duas pontas que o `<input type="date">` deve oferecer.
 *
 * São derivadas de `IDADE_MINIMA` e `IDADE_MAXIMA`, e não escritas à mão: o dia em que o piso
 * etário mudar, o calendário muda com ele. Um `max` fixo é a forma mais barata de o campo e a
 * regra discordarem sem ninguém notar.
 */
export function limitesDaDataDeNascimento(hoje = dayjs()): { min: string; max: string } {
  return {
    // Nascer hoje menos 6 anos é o ÚLTIMO dia que passa pelo portão. Um dia depois, a conta é
    // recusada — então é este o dia que o calendário deve parar de oferecer.
    max: hoje.subtract(IDADE_MINIMA, "year").format("YYYY-MM-DD"),
    // AS DUAS PONTAS NAO SAO SIMETRICAS, e o teste de limite foi quem mostrou. O piso e
    // "pelo menos 6 anos" e o teto e "no maximo 120": quem nasceu ha exatamente 121 anos menos
    // um dia AINDA TEM 120, e passa. Entao a data mais antiga aceita e 121 anos atras mais um dia,
    // e nao 120 anos atras — que era o que eu tinha escrito, e recusava gente que a regra aceita.
    min: hoje.subtract(IDADE_MAXIMA + 1, "year").add(1, "day").format("YYYY-MM-DD"),
  };
}

/**
 * De quantos a quantos caracteres a senha pode ter.
 *
 * O teto era 15, e o relatório está certo sobre o custo: **15 caracteres impedem frase-senha**, que
 * é justamente a senha que uma criança lembra e ninguém adivinha. O piso continua 6.
 *
 * O teto existe, e não é decoração: o Firebase aceita senha longa, mas o campo é digitado no
 * celular da escola, e um campo sem teto aceita colagem de texto inteiro por acidente.
 */
export const SENHA = { minimo: 6, maximo: 64 } as const;

/** "De 6 a 64 caracteres — pode ser uma frase." */
export function dicaDeSenha(): string {
  return `De ${SENHA.minimo} a ${SENHA.maximo} caracteres — pode ser uma frase.`;
}

// ═══════════════════════════════════════════════════════════════════════════════════════════
// QUEM ACEITA, QUANDO QUEM PREENCHE É MENOR DE IDADE — T9.7
//
// ── O ACHADO ────────────────────────────────────────────────────────────────────────────────
//
// O QA, em 02/10/2026, pondo uma data de nascimento de 2017:
//
//     idade mínima que o formulário aceita ......................... 6 anos
//     idade calculada no teste ..................................... 9 anos
//     campos de responsável que aparecem ........................... nenhum
//     vezes que a palavra "responsável" aparece na tela de cadastro . 0
//     quem marca o aceite .......................................... a própria criança
//
// *"A tela responde 'Você tem 9 anos.' e segue igual (…) a única coisa a marcar é a caixa do
// aceite dos termos — marcada pela própria criança. Os termos, na página deles, dizem que o uso
// por menor de idade deve acontecer com acompanhamento da escola ou de um responsável."*
//
// O defeito é de COERÊNCIA, e é por isso que ele cabe aqui: a plataforma já escreveu, numa página
// que ela mesma publicou e que o cadastro manda aceitar, uma condição que o cadastro não menciona.
// A tela pergunta a idade, calcula, diz em voz alta — e não usa o que descobriu.
//
// ── O QUE ESTA FUNÇÃO FAZ, E O QUE ELA DELIBERADAMENTE NÃO FAZ ──────────────────────────────
//
// O achado propõe *"pedir nome e contato de um responsável e deixar o aceite com ele"*. Isso é
// COLETAR DADO NOVO sobre um adulto por causa de uma criança, e numa plataforma escolar isso é
// decisão com consequência de LGPD — não é escolha de quem escreve a tela. As três páginas legais
// estão publicadas marcadas como rascunho, à espera de revisão jurídica (T9.10), e é exatamente
// esse o parecer que diria se o fluxo de autorização é por e-mail, pela escola, ou de outro jeito.
//
// A outra metade do achado é a que esta função entrega, na letra dele: *"ou dizer com todas as
// letras que quem aceita tem de ser ele"*. Isso não coleta nada, não decide nada, e faz a tela
// dizer o que o documento que ela manda aceitar já diz.
//
// A página de Termos, hoje, é honesta sobre o buraco: *"Não existe, hoje, um fluxo de autorização
// do responsável."* Esta frase na tela de cadastro não fecha o buraco — ela para de escondê-lo.

/** A partir de quantos anos a pessoa aceita sozinha. */
export const MAIORIDADE = 18;

/**
 * O que a tela de cadastro diz sobre quem aceita, ou `null` quando não há o que dizer.
 *
 * `null` para adulto, para data vazia e para data que não dá para ler — a tela não especula sobre
 * quem não respondeu.
 */
export function recadoDeQuemAceita(nascimento: string, hoje = new Date()): string | null {
  const anos = idadeEmAnos(nascimento, hoje);
  if (anos === null || anos < 0) return null;
  if (anos >= MAIORIDADE) return null;
  return (
    "Você ainda não tem 18 anos. Pelos Termos de uso, o HQ-Lab se usa com acompanhamento da " +
    "escola ou de um responsável, e é essa pessoa adulta que aceita os Termos. Chame ela para " +
    "ler com você antes de marcar."
  );
}
